/* eslint-disable no-use-before-define */
import { differenceInDays } from 'date-fns';
import {
  assoc,
  assocPath,
  dissoc,
  find,
  includes,
  is,
  isEmpty,
  map,
  merge,
  mergeDeepRight,
  not,
  omit,
  pathOr,
  pick,
  prop,
  propEq,
  sortBy,
  isNil,
  filter,
  last,
  allPass
} from 'ramda';

import coreApi from '../coreApi/coreApiService';
import { decodeJWT, jwtExpired } from '../utils/jwt';
import * as webStorage from '../webStorage';
import { requestCaptchaToken } from './recaptcha';
import { eventTags } from '../services/analytics/tags';
import piwik from '../services/analytics/analytics';

import { getAppointment } from '../selectors/appointment';
import { getCarDestination } from '../selectors/carDestination';
import {
  getAuth,
  getClaim,
  getClaimAddress,
  getClaimId,
  getClaimPosition,
  getMission,
  getMissionId,
  getPreviousClaimId,
  getPreviousMissionId,
  getScopeStatus,
  getCarDestinations as getRecommendedCarDestinations,
  getAllowHidingDestinationProcessInDigital,
  getCoreRentalMissionId,
  getCoreRentalMission,
  getEventCode,
  getBenefit,
  getBusinessPartnerCode
} from '../selectors/coreApi';
import { getEvent, getEventDetails } from '../selectors/diagnosis';
import {
  getBrand,
  getCountry,
  getFirstName,
  getLastName,
  getModel,
  getEnergy,
  getEnergyType,
  getMileage,
  getOtp,
  getPhoneNumber,
  getPlateNumber,
  getVinNumber,
  getRegistrationDate,
  getType,
  getWeight,
  getLeaserContractNumber
} from '../selectors/eligibility';
import { getErrorCode } from '../selectors/error';
import { getFnol } from '../selectors/fnol';
import { getSession, getNpsAnswer } from '../selectors/global';
import {
  getHighwayMetres,
  getLocation,
  getPosition,
  getCountry as getLocationCountry,
  getHighwayCode,
  getHighwayDirection
} from '../selectors/location';
import { getEventSource } from '../selectors/missionMonitoring';
import { getDisplayVinNumber, getIsPublicApiV5, isVinMandatory } from '../selectors/settings';
import {
  getRentalDrivers,
  getRentalVehicleDetails,
  getRentalBookingDetails,
  getRentalVehicleOtherGarageName,
  getRentalVehicleOtherGarageEmail,
  getRentalVehicleOtherGaragePhone,
  getRentalVehicleOtherLocationAddressObject,
  getRentalExtensionDurationInMinutes
} from '../selectors/rentalMission';
import { getCaptchaV2Token } from '../selectors/recaptcha';

import {
  FNOL_CUSTOMER_ANSWER,
  MISSION_TYPE,
  VEHICLE_DESINATION_REASON,
  VEHICLE_DESINATION_TYPE,
  ERROR_CODES,
  MISSION_STATUS,
  HISTORY_EVENTS,
  BENEFITS,
  CAPTCHA_VERSION
} from '../enums';
import {
  buildApiContext,
  extractMissionHistory,
  extractUniqueClaimHistory,
  isMissionMonitoringIsEnded,
  isROSMission,
  isRentalMissionOngoing,
  isRentalService,
  isTOWMission,
  lastMissionTowOrRos
} from '../utils/coreApi';

export const RESET_RETRY_COUNTER = 'DRSA/COREAPI/RESET_REQUEST_COUNTER';
export const RETRY_NOT_FOUND_MODAL = 'DRSA/COREAPI/RETRY_NOT_FOUND_MODAL';
export const RETRY_EXPIRED_MODAL = 'DRSA/COREAPI/RETRY_EXPIRED_MODAL';
export const RETRY_CLAIM_EXIST = 'DRSA/COREAPI/RETRY_CLAIM_EXIST';
export const START_REQUEST = 'DRSA/COREAPI/START_REQUEST';
export const SESSION_ERROR = 'DRSA/COREAPI/SESSION_ERROR';
export const REQUEST_ERROR = 'DRSA/COREAPI/REQUEST_ERROR';
export const REQUEST_SUCCESS = 'DRSA/COREAPI/REQUEST_SUCCESS';
export const AUTH_SUCCESS = 'DRSA/COREAPI/AUTH_SUCCESS';
export const AUTH_ERROR = 'DRSA/COREAPI/AUTH_ERROR';
export const SCOPE_ERROR = 'DRSA/COREAPI/SCOPE_ERROR';
export const AUTHENTICATED = 'DRSA/COREAPI/AUTHENTICATED';
export const IDENTIFIED = 'DRSA/COREAPI/IDENTIFIED';
export const APPROVED = 'DRSA/COREAPI/APPROVED';
export const CLAIM_CREATED = 'DRSA/COREAPI/CLAIM_CREATED';
export const PATCH_CLAIM = 'DRSA/COREAPI/PATCH_CLAIM';
export const CLAIM_PATCHED = 'DRSA/COREAPI/CLAIM_PATCHED';
export const CLAIM_LOADED = 'DRSA/COREAPI/CLAIM_LOADED';
export const RENTAL_CLAIM_LOADED = 'DRSA/COREAPI/RENTAL_CLAIM_LOADED';
export const NO_CLAIM_FOUND = 'DRSA/COREAPI/NO_CLAIM_FOUND';
export const CLAIM_ERROR = 'DRSA/COREAPI/CLAIM_ERROR';
export const MISSION_LOADED = 'DRSA/COREAPI/MISSION_LOADED';
export const MISSIONS_LOADED = 'DRSA/COREAPI/MISSIONS_LOADED';
export const NO_MISSION_FOUND = 'DRSA/COREAPI/NO_MISSION_FOUND';
export const CREATE_MISSION = 'DRSA/COREAPI/CREATE_MISSION';
export const MISSION_CREATED = 'DRSA/COREAPI/MISSION_CREATED';
export const CREATE_RENTAL_MISSION = 'DRSA/COREAPI/CREATE_RENTAL_MISSION';
export const RENTAL_MISSION_CREATED = 'DRSA/COREAPI/RENTAL_MISSION_CREATED';
export const RENTAL_MISSION_UPDATED = 'DRSA/COREAPI/RENTAL_MISSION_UPDATED';
export const RENTAL_MISSION_CANCELED = 'DRSA/COREAPI/RENTAL_MISSION_CANCELED';
export const RENTAL_EXTENSION_CANCELED = 'DRSA/COREAPI/RENTAL_EXTENSION_CANCELED';
export const RENTAL_EXTENSION_SENT = 'DRSA/COREAPI/RENTAL_EXTENSION_SENT';
export const MISSION_DISPATCHED = 'DRSA/COREAPI/MISSION_DISPATCHED';
export const RENTAL_MISSION_DISPATCHED = 'DRSA/COREAPI/RENTAL_MISSION_DISPATCHED';
export const MISSION_PATCHED = 'DRSA/COREAPI/MISSION_PATCHED';
export const MISSION_CONFIRMED = 'DRSA/COREAPI/MISSION_CONFIRMED';
export const MISSION_STARTED = 'DRSA/COREAPI/MISSION_STARTED';
export const CANCEL_MISSION = 'DRSA/COREAPI/CANCEL_MISSION';
export const MISSION_CANCELED = 'DRSA/COREAPI/MISSION_CANCELED';
export const REQUEST_CAR_DESTINATIONS = 'DRSA/COREAPI/REQUEST_CAR_DESTINATIONS';
export const REQUEST_SPECIFIC_CAR_DESTINATIONS = 'REQUEST_SPECIFIC_CAR_DESTINATIONS';
export const REQUEST_CAR_DESTINATIONS_RULES = 'DRSA/COREAPI/REQUEST_CAR_DESTINATIONS_RULES';
export const CAR_DESTINATIONS = 'DRSA/COREAPI/CAR_DESTINATIONS';
export const SPECIFIC_CAR_DESTINATIONS = 'DRSA/COREAPI/SPECIFIC_CAR_DESTINATION';
export const CAR_DESTINATIONS_RULES = 'DRSA/COREAPI/CAR_DESTINATIONS_RULES';
export const CAR_DESTINATIONS_INFO_LOADED = 'DRSA/COREAPI/CAR_DESTINATIONS_INFO_LOADED';
export const DEC_REQUEST_COUNTER = 'DRSA/COREAPI/DEC_REQUEST_COUNTER';
export const INC_REQUEST_COUNTER = 'DRSA/COREAPI/INC_REQUEST_COUNTER';
export const MISSION_MONITORING_STARTED = 'DRSA/COREAPI/MISSION_MONITORING_STARTED';
export const MISSION_MONITORING_ENDED = 'DRSA/COREAPI/MISSION_MONITORING_ENDED';
export const MISSION_MONITORING_ENDING = 'DRSA/COREAPI/MISSION_MONITORING_ENDING';
export const MISSION_MONITORING_EVENT = 'DRSA/COREAPI/MISSION_MONITORING_EVENT';
export const TASK_CREATED = 'DRSA/COREAPI/TASK_CREATED';
export const PERSON_SPECIAL_NEEDS_ERROR = 'DRSA/COREAPI/PERSON_SPECIAL_NEEDS_ERROR';
export const NO_CAR_DESTINATION = 'DRSA/COREAPI/NO_CAR_DESTINATION';
export const DIGITAL_INTERACTIONS = 'DRSA/COREAPI/DIGITAL_INTERACTIONS';
export const VOICE_EXIT_ERROR = 'DRSA/COREAPI/VOICE_EXIT_ERROR';
export const CREATE_MISSION_ERROR = 'DRSA/COREAPI/CREATE_MISSION_ERROR';
export const EVENTS_DETAILS_ERROR = 'DRSA/COREAPI/EVENTS_DETAILS_ERROR';
export const VEHICLE_LOCATION_ERROR = 'DRSA/COREAPI/VEHICLE_LOCATION_ERROR';
export const CAR_DESTINATION_ERROR = 'DRSA/COREAPI/CAR_DESTINATION_ERROR';
export const CONFIRMATION_ERROR = 'DRSA/COREAPI/CONFIRMATION_ERROR';
export const CONFIRMATION_ERROR_CALL_ASSISTANCE = 'DRSA/COREAPI/CONFIRMATION_ERROR_CALL_ASSISTANCE';
export const MISSION_MONITORING_ERROR = 'DRSA/COREAPI/MISSION_MONITORING_ERROR';
export const PROVIDER_ARRIVAL_ERROR = 'DRSA/COREAPI/PROVIDER_ARRIVAL_ERROR';
export const CLEAR_MISSION_MONITORING = 'DRSA/COREAPI/CLEAR_MISSION_MONITORING';
export const LOCATION_DETAILS_ERROR = 'DRSA/COREAPI/LOCATION_DETAILS_ERROR';
export const LOCATION_DETAILS = 'DRSA/COREAPI/LOCATION_DETAILS';
export const CANCEL_APPOINTMENT = 'DRSA/COREAPI/CANCEL_APPOINTMENT';
export const UPDATE_APPOINTMENT = 'DRSA/COREAPI/UPDATE_APPOINTMENT';
export const CONTINUE_PROCESS = 'DRSA/COREAPI/CONTINUE_PROCESS';
export const CANCEL_MILESTONE = 'DRSA/COREAPI/CANCEL_MILESTONE';
export const GET_HIGHWAY_SECTIONS = 'DRSA/COREAPI/GET_HIGHWAY_SECTIONS';
export const HIGHWAY_SECTIONS_FOUND = 'DRSA/COREAPI/HIGHWAY_SECTIONS_FOUND';
export const CANCEL_HIGHWAY_DIRECTION = 'DRSA/COREAPI/CANCEL_HIGHWAY_DIRECTION';
export const HIGHWAY_SECTIONS_NOT_FOUND = 'DRSA/COREAPI/HIGHWAY_SECTIONS_NOT_FOUND';
export const ANOTHER_REQUEST = 'DRSA/COREAPI/ANOTHER_REQUEST';
export const RENTAL_BENEFIT_COVERAGE_ISSUE = 'DRSA/COREAPI/RENTAL_BENEFIT_COVERAGE_ISSUE';
export const RENTAL_MISSION_ALREADY_EXISTS = 'DRSA/COREAPI/RENTAL_MISSION_ALREADY_EXISTS';
export const CANCEL_PREVIOUS_CLAIM_HANDLING = 'DRSA/COREAPI/CANCEL_PREVIOUS_CLAIM_HANDLING';
export const VEHICLE_INFO_LOADED = 'DRSA/COREAPI/VEHICLE_INFO_LOADED';
export const CLAIM_VEHICLE_LOSS_PUTTED = 'DRSA/COREAPI/CLAIM_VEHICLE_LOSS_PUTTED';
export const PUT_CLAIM = 'DRSA/COREAPI/PUT_CLAIM';
export const CLAIM_PUTTED = 'DRSA/COREAPI/CLAIM_PUTTED';
export const MISSION_PUTTED = 'DRSA/COREAPI/MISSION_PUTTED';
export const CLEAR_VEHICLE_DESTINATION_VALUES = 'DRSA/COREAPI/CLEAR_VEHICLE_DESTINATION_VALUES';
export const CLAIM_DIGITAL_EXIT_CREATED = 'DRSA/COREAPI/CLAIM_DIGITAL_EXIT_CREATED';
export const OTP_REQUESTED = 'DRSA/COREAPI/OTP_REQUESTED';
export const IDENTIFICATION_REQUEST_EXCEEDED = 'DRSA/COREAPI/IDENTIFICATION_REQUEST_EXCEEDED';
export const JWT_CREATED = 'DRSA/COREAPI/JWT_CREATED';
export const NPS_ELIGIBILITY_CLAIM = 'DRSA/COREAPI/NPS_ELIGIBILITY_CLAIM';
export const PUT_NPS_ELIGIBILITY_CLAIM = 'DRSA/COREAPI/PUT_NPS_ELIGIBILITY_CLAIM';
export const CLEAR_JWT = 'DRSA/COREAPI/CLEAR_JWT';
export const PRODUCT_CONFIGURATION_LOADED = 'DRSA/COREAPI/PRODUCT_CONFIGURATION_LOADED';
export const CLAIM_OPERATION_LOADED = 'DRSA/CORE_API/CLAIM_OPERATION_LOADED';
export const EVENT_ADDITIONAL_QUESTIONS_LOADED = 'DRSA/CORE_API/EVENT_ADDITIONAL_QUESTIONS_LOADED';
export const CLAIM_HISTORY_DISPATCH = 'DRSA/COREAPI/CLAIM_HISTORY_DISPATCH';
export const CSC_ELIGIBILE = 'DRSA/COREAPI/CSC_ELIGIBILE';
export const IMMOBILISATION_GARAGE_UPDATED = 'DRSA/COREAPI/IMMOBILISATION_GARAGE_UPDATED';
export const VIEW_RENTAL_SUMMARY = 'DRSA/COREAPI/VIEW_RENTAL_SUMMARY';
export const VIEW_TIMELINE = 'DRSA/COREAPI/VIEW_TIMELINE';
export const TIMEOUT = 'DRSA/COREAPI/TIMEOUT';
export const SELECT_BENEFIT = 'DRSA/COREAPI/SELECT_BENEFIT';
export const CLEAR_BENEFIT = 'DRSA/COREAPI/CLEAR_BENEFIT';
export const RENTAL_EXTENSION_WORKFLOW = 'DRSA/CORE_API/RENTAL_EXTENSION_WORKFLOW';
export const PHONE_CONTACT = 'DRSA/CORE_API/PHONE_CONTACT';
export const RENTAL_CURRENT_LIMITS = 'DRSA/CORE_API/RENTAL_CURRENT_LIMITS';
export const CSC_SETTINGS_LOADED = 'DRSA/CORE_API/CSC_SETTINGS_LOADED';
export const CAPTCHA_JWT_CREATED = 'DRSA/CORE_API/CAPTCHA_JWT_CREATED';
export const CAPTCHA_JWT_INVALID = 'DRSA/CORE_API/CAPTCHA_JWT_INVALID';
export const REQUIRE_V2_CAPTCHA_VERIFICATION = 'DRSA/CORE_API/REQUIRE_V2_CAPTCHA_VERIFICATION';
export const REQUEST_V2_CAPTCHA_VERIFICATION = 'DRSA/CORE_API/REQUEST_V2_CAPTCHA_VERIFICATION';
export const CLEAN_UP_REQUIRED_V2_CAPTCHA_VERIFICATION = 'DRSA/CORE_API/CLEAN_UP_REQUIRED_V2_CAPTCHA_VERIFICATION';
export const HIGHWAY_NOT_KNOWN = 'DRSA/CORE_API/HIGHWAY_NOT_KNOWN';
export const HIGHWAYS_NOT_FOUND = 'DRSA/CORE_API/HIGHWAYS_NOT_FOUND';
export const STORE_HIGHWAYS = 'DRSA/CORE_API/STORE_HIGHWAYS';
export const CLEAR_HIGHWAYS_STATE = 'DRSA/CORE_API/CLEAR_HIGHWAYS_STATE';
export const HIGHWAY_DIRECTIONS_NOT_FOUND = 'DRSA/CORE_API/HIGHWAY_DIRECTIONS_NOT_FOUND';
export const STORE_HIGHWAY_DIRECTIONS = 'DRSA/CORE_API/STORE_HIGHWAY_DIRECTIONS';
export const HIGHWAY_POINT_OF_INTEREST_DIGITAL_EXIT = 'DRSA/CORE_API/HIGHWAY_POINT_OF_INTEREST_DIGITAL_EXIT';
export const HIGHWAY_POINT_OF_INTEREST_NOT_LISTED = 'DRSA/CORE_API/HIGHWAY_POINT_OF_INTEREST_NOT_LISTED';
export const HIGHWAY_POINT_OF_INTERESTS_NOT_FOUND = 'DRSA/CORE_API/HIGHWAY_POINT_OF_INTERESTS_NOT_FOUND';
export const STORE_HIGHWAY_POINT_OF_INTERESTS = 'DRSA/CORE_API/STORE_HIGHWAY_POINT_OF_INTERESTS';
export const CONFIRM_HIGHWAY_LOCATION_WITHOUT_HIGHWAY_FROM_REFERENTIAL =
  'DRSA/CORE_API/CONFIRM_HIGHWAY_LOCATION_WITHOUT_HIGHWAY_FROM_REFERENTIAL';
export const TRANSFER_TO_OEM = 'DRSA/COREAPI/TRANSFER_TO_OEM';
export const OEM_TRANSFERRED = 'DRSA/COREAPI/OEM_TRANSFERRED';

export const errors = {
  [SCOPE_ERROR]: 'SCOPE_ERROR',
  [AUTH_ERROR]: 'AUTH_ERROR',
  [CLAIM_ERROR]: 'CLAIM_ERROR',
  [PERSON_SPECIAL_NEEDS_ERROR]: 'PERSON_SPECIAL_NEEDS_ERROR',
  [NO_CAR_DESTINATION]: 'NO_CAR_DESTINATION',
  [CREATE_MISSION_ERROR]: 'CREATE_MISSION_ERROR',
  [EVENTS_DETAILS_ERROR]: 'EVENTS_DETAILS_ERROR',
  [VEHICLE_LOCATION_ERROR]: 'VEHICLE_LOCATION_ERROR',
  [CAR_DESTINATION_ERROR]: 'CAR_DESTINATION_ERROR',
  [CONFIRMATION_ERROR]: 'CONFIRMATION_ERROR',
  [CONFIRMATION_ERROR_CALL_ASSISTANCE]: 'CONFIRMATION_ERROR_CALL_ASSISTANCE',
  [MISSION_MONITORING_ERROR]: 'MISSION_MONITORING_ERROR',
  [PROVIDER_ARRIVAL_ERROR]: 'PROVIDER_ARRIVAL_ERROR',
  [LOCATION_DETAILS_ERROR]: 'LOCATION_DETAILS_ERROR',
};

export const CAR_DESTINATION_NOT_FOUND = 404;

export const digitalExitReasonsToBeCalledBack = [
  ERROR_CODES.CLAIM_EVENT_MISSING,
  ERROR_CODES.CLAIM_LOCATION_MISSING,
  ERROR_CODES.CLAIM_EVENT_DETAILS_MISSING,
  ERROR_CODES.CLAIM_LOCATION_DETAILS_MISSING,
  ERROR_CODES.MISSION_ALREADY_EXISTS,
  ERROR_CODES.CLAIM_ANTERIORITY_ISSUE,
  ERROR_CODES.CLAIM_COVERAGE_ISSUE,
  ERROR_CODES.HOME_DISTANCE_ISSUE,
  ERROR_CODES.CLAIM_WARRANTY_ISSUE,
  ERROR_CODES.CRANE_REQUIRED,
  ERROR_CODES.VEHICLE_IS_ON_A_HIGHWAY,
  ERROR_CODES.NO_SUITABLE_SERVICE_FOUND,
  ERROR_CODES.MISSION_OVERCOST,
  ERROR_CODES.SERVICE_DOESNT_ALLOW_DISPATCH,
  ERROR_CODES.HIGHWAY_SECTIONS_NON_AVAILABLE,
  ERROR_CODES.CANCEL_PREVIOUS_CLAIM_HANDLING,
  ERROR_CODES.CANCEL_MILESTONE,
  ERROR_CODES.CANCEL_HIGHWAY_DIRECTION,
  ERROR_CODES.CANCEL_APPOINTMENT,
  ERROR_CODES.UPDATE_APPOINTMENT,
  ERROR_CODES.PROVIDER_NOT_ON_SPOT,
  ERROR_CODES.CUSTOMER_NEEDS_ADDITIONAL_SERVICE,
  ERROR_CODES.UNKNOWN_DESTINATION,
  ERROR_CODES.INCORRECT_VEHICLE_DESTINATION,
  ERROR_CODES.ADDITIONAL_TRANSPORTATION_NEEDED,
  ERROR_CODES.BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.EVENT_TO_POLICY_ADDRESS_EXCEEDED,
  ERROR_CODES.OVER_DISTANCE_LIMIT_EXCEEDED,
  ERROR_CODES.VEHICLE_IS_ON_AN_UNAUTHORIZED_HIGHWAY,
  ERROR_CODES.FRAUD_ALERT,
  ERROR_CODES.TECHNICAL_ERROR,
  ERROR_CODES.RENTAL_BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.BO_CSC_BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.RENTAL_CAR_BENEFITS_NOT_COVERED,
  ERROR_CODES.MISSING_REFERENTIAL_HIGHWAY_INFORMATION,
  ERROR_CODES.CUSTOMER_DOES_NOT_KNOW_THE_HIGHWAY,
  ERROR_CODES.HIGHWAY_POINT_OF_INTEREST_SHOULD_EXIT,
  ERROR_CODES.MISSING_POINT_OF_INTEREST_INFORMATION,
  ERROR_CODES.CUSTOMER_INSISTS_ON_HIGHWAY_LOCATION,
  ERROR_CODES.RENTAL_AGENCY_CLOSED,
  ERROR_CODES.VEHICLE_INFO_REQUIRED,
];

const initialState = { hasTechnicalError: false, retryCounter: {}, pendingRequests: 0 };

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    /* ---- Authentication and Captcha ---- */
    case AUTH_SUCCESS:
    case JWT_CREATED:
      return {
        ...state,
        ...action.payload,
      };
    case CLEAR_JWT:
      return {
        ...state,
        jwt: undefined,
      };
    case REQUIRE_V2_CAPTCHA_VERIFICATION:
      return {
        ...state,
        captchaV2Required: true,
      };
    case CLEAN_UP_REQUIRED_V2_CAPTCHA_VERIFICATION:
      return {
        ...state,
        captchaV2Required: false,
      };
    case CAPTCHA_JWT_CREATED:
      return {
        ...state,
        captchaJwt: action.captchaJwt,
      };
    /* ---- Product ---- */
    case PRODUCT_CONFIGURATION_LOADED:
      return { ...state, productConfiguration: action.configuration };
    /* ---- Claim, Location, Highway, Eligibility, Immobilization ---- */
    case CLAIM_OPERATION_LOADED:
      return {
        ...state,
        claimOperation: action.claimOperation,
      };
    case EVENT_ADDITIONAL_QUESTIONS_LOADED:
      return {
        ...state,
        eventAdditionalQuestions: action.eventAdditionalQuestions,
      };
    case CLAIM_PATCHED:
    case CLAIM_CREATED:
      return {
        ...state,
        claim: action.claim,
      };
    case CLAIM_LOADED: {
      const oemProviderPhone = action.claim?.oemProvider?.phoneNumber;
      return {
        ...state,
        claim: action.claim,
        ...(oemProviderPhone && { oemProvider: { phoneNumber: oemProviderPhone } }),
      };
    }
    case CLAIM_PUTTED:
      return {
        ...state,
        claim: { ...state.claim, ...action.claim },
      };
    case NPS_ELIGIBILITY_CLAIM:
      return {
        ...state,
        ...action.payload,
      };
    case RETRY_CLAIM_EXIST:
      return {
        ...state,
        retryClaimExist: not(state.retryClaimExist),
      };
    case HIGHWAY_SECTIONS_FOUND:
      return {
        ...state,
        highwaySections: action.sections,
      };
    case STORE_HIGHWAYS:
      return {
        ...state,
        highways: action.payload,
      };
    case HIGHWAYS_NOT_FOUND:
      return {
        ...state,
        highways: [],
      };
    case CLEAR_HIGHWAYS_STATE:
      return {
        ...state,
        highways: undefined,
      };
    case STORE_HIGHWAY_DIRECTIONS:
      return {
        ...state,
        highwayDirections: action.payload,
      };
    case STORE_HIGHWAY_POINT_OF_INTERESTS:
      return {
        ...state,
        highwayPointOfInterests: action.payload,
      };
    case VEHICLE_INFO_LOADED:
      return assocPath(['claim', 'vehicle'], action.vehicle, state);
    case CLEAR_VEHICLE_DESTINATION_VALUES:
      return {
        ...state,
        mission: omit(['vehicleDestination'], state.mission),
      };
    case CLAIM_HISTORY_DISPATCH: {
      const claimHistory = action.history?.claimHistory ? extractUniqueClaimHistory(action.history?.claimHistory) : [];
      const missionHistory = action.history?.missionHistory;

      const missionHistoryForTimeline = missionHistory ? extractMissionHistory(missionHistory, state) : [];

      const extractedInformationForRentalMission = extractRentalMissionAdditionalInformationFromHistoryEvent(missionHistory, state);

      return {
        ...state,
        claimHistoryCurrent: { claimHistory, missionHistory: missionHistoryForTimeline },
        rentalMission: extractedInformationForRentalMission
          ? mergeDeepRight(state.rentalMission, extractedInformationForRentalMission)
          : state.rentalMission,
      };
    }
    case SELECT_BENEFIT:
      return {
        ...state,
        selectedBenefit: action.payload,
      };
    case CLEAR_BENEFIT:
      return {
        ...state,
        selectedBenefit: null,
      };
    case CSC_ELIGIBILE:
      return {
        ...state,
        cscEligibility: action.response,
      };
    case TASK_CREATED:
      return {
        ...state,
        task: action.task,
      };
    /* ---- Request handling ---- */
    case START_REQUEST:
      return {
        ...state,
        pendingRequests: state.pendingRequests + 1,
        retryCounter: assoc(action.request.method, pathOr(0, ['retryCounter', action.request.method], state) + 1, state.retryCounter),
      };
    case REQUEST_SUCCESS:
      return {
        ...state,
        pendingRequests: state.pendingRequests - 1,
      };
    case REQUEST_ERROR:
    case DEC_REQUEST_COUNTER:
      return {
        ...state,
        pendingRequests: state.pendingRequests - 1,
      };
    case INC_REQUEST_COUNTER:
      return {
        ...state,
        pendingRequests: state.pendingRequests + 1,
      };
    case RESET_RETRY_COUNTER:
      return {
        ...state,
        retryCounter: dissoc(action.request.method, state.retryCounter),
      };
    case RETRY_NOT_FOUND_MODAL:
      return {
        ...state,
        retryNotFoundModal: not(state.retryNotFoundModal),
      };
    case RETRY_EXPIRED_MODAL:
      return {
        ...state,
        retryExpiredModal: not(state.retryExpiredModal),
      };
    /* ---- Mission (1st and common) ---- */
    case MISSION_MONITORING_EVENT:
      return {
        ...state,
        mission: action?.payload?.mainMission ? mergeDeepRight(state.mission, action?.payload?.mainMission) : state.mission,
        missionMonitoring: {
          ...(action?.payload?.mainMission
            ? mergeDeepRight(state.missionMonitoring, action?.payload?.mainMission)
            : state.missionMonitoring),
          id: action.id,
          timestamp: action.timestamp,
        },
        rentalMission: action?.payload?.rentalMission
          ? mergeDeepRight(state.rentalMission, action?.payload?.rentalMission)
          : state.rentalMission,
      };
    case MISSION_MONITORING_STARTED:
      return {
        ...state,
        missionMonitoring: {
          ...state.missionMonitoring,
          eventSource: action.eventSource,
        },
      };
    case CLEAR_MISSION_MONITORING:
      return {
        ...state,
        missionMonitoring: {
          eventSource: state.missionMonitoring.eventSource,
        },
      };
    case CAR_DESTINATIONS_RULES:
      return {
        ...state,
        carDestinationsRules: action.rules,
      };
    case CAR_DESTINATIONS:
      return {
        ...state,
        carDestinations: action.destinations,
      };
    case REQUEST_SPECIFIC_CAR_DESTINATIONS:
      return {
        ...state,
        specificDestinationsLoaded: false,
      };
    case SPECIFIC_CAR_DESTINATIONS:
      return {
        ...state,
        specificDestinationsLoaded: true,
      };
    case MISSIONS_LOADED:
      return {
        ...state,
        missions: action.missions,
        rentalMission: {
          ...state.rentalMission,
          ...last(filter(allPass([isRentalService, isRentalMissionOngoing]))(action.missions)),
        },
      };
    case MISSION_LOADED:
    case MISSION_CREATED:
    case MISSION_DISPATCHED:
    case MISSION_PATCHED:
      return {
        ...state,
        mission: action.mission,
      };
    case MISSION_PUTTED:
      return {
        ...state,
        mission: { ...state.mission, ...action.mission },
      };
    case MISSION_CANCELED:
      return {
        ...state,
        mission: undefined,
        lastCancelledMissionId: action.id,
      };
    /* ---- Rental ---- */
    case RENTAL_MISSION_CREATED:
    case RENTAL_MISSION_DISPATCHED:
      return {
        ...state,
        rentalMission: action.mission,
      };
    case RENTAL_MISSION_CANCELED:
      // No need to update 'lastCancelledMissionId', since that is used for the main mission workflow
      return {
        ...state,
        rentalMission: undefined,
      };
    case RENTAL_MISSION_UPDATED:
      return {
        ...state,
        rentalMission: { ...state.mission, ...action.mission },
      };
    case RENTAL_CURRENT_LIMITS:
      return {
        ...state,
        rentalCurrentLimits: action.response,
      };
    case CSC_SETTINGS_LOADED:
      return {
        ...state,
        cscSettings: action.settings,
      };
    case OEM_TRANSFERRED:
      return {
        ...state,
        oemProvider: action.oemProvider,
      };
    default:
      return state;
  }
};

export const decRequestCounter = () => ({ type: DEC_REQUEST_COUNTER });
export const incRequestCounter = () => ({ type: INC_REQUEST_COUNTER });
export const startRequest = request => ({ type: START_REQUEST, request });
export const requestError = ({ method, errorCode, statusCode, errorMessage }) => ({
  type: REQUEST_ERROR,
  error: { method, errorCode, statusCode, errorMessage },
});
export const requestSuccess = request => ({ type: REQUEST_SUCCESS, request });
export const resetRetryCounter = (request = {}) => ({ type: RESET_RETRY_COUNTER, request });

export const retryNotFoundModal = () => dispatch => dispatch({ type: RETRY_NOT_FOUND_MODAL });
export const retryExpiredModal = () => dispatch => dispatch({ type: RETRY_EXPIRED_MODAL });
export const retryClaimExist = () => dispatch => dispatch({ type: RETRY_CLAIM_EXIST });

export const clearMissionMonitoring = () => dispatch => dispatch({ type: CLEAR_MISSION_MONITORING });

export const cancelAppointment = () => ({ type: CANCEL_APPOINTMENT });
export const updateAppointment = () => ({ type: UPDATE_APPOINTMENT });

export const continueProcess = () => ({ type: CONTINUE_PROCESS });

export const cancelMilestone = () => ({ type: CANCEL_MILESTONE });
export const cancelHighwayDirection = () => ({ type: CANCEL_HIGHWAY_DIRECTION });

export const anotherRequest = () => ({ type: ANOTHER_REQUEST });
export const cancelPreviousClaimHandling = () => ({ type: CANCEL_PREVIOUS_CLAIM_HANDLING });
export const rentalBenefitCoverageIssue = () => ({ type: RENTAL_BENEFIT_COVERAGE_ISSUE });
export const rentalMissionAlreadyExists = () => ({ type: RENTAL_MISSION_ALREADY_EXISTS });

export const requestV2CaptchaVerification = () => ({ type: REQUEST_V2_CAPTCHA_VERIFICATION });

export const refresh = () => () => {
  window.location.reload();
};

const requestCoreApi = (dispatch, ctx, { errorCode, refreshAuthToken = false } = {}) => {
  const { method } = ctx;
  dispatch(startRequest({ method }));
  return coreApi(ctx)
    .then(res => {
      dispatch(resetRetryCounter({ method }));
      dispatch(requestSuccess({ method }));
      return res;
    })
    .catch(err => {
      if (err.response) {
        const { data, status } = err.response;

        const { errorCode: resErrorCode, errorMessage: resErrorMessage, error: resError } = data || {};

        if (status === 401) {
          const isTokenExpired = jwtExpired(decodeJWT(ctx.auth?.accessToken));
          const isInvalidToken = resError === 'invalid_token' || isTokenExpired;

          if (refreshAuthToken || isInvalidToken) {
            dispatch(decRequestCounter());

            return dispatch(authenticate())
              .then(newAuth => requestCoreApi(dispatch, { ...ctx, auth: { ...newAuth, jwt: ctx.auth?.jwt } }, { refreshAuthToken: false }))
              .catch(() => {
                piwik.trackEvent(eventTags.session_error);
                dispatch(sessionError());
              });
          }

          if (resErrorCode === ERROR_CODES.CAPTCHA_JWT_INVALID) {
            dispatch({ type: CAPTCHA_JWT_INVALID });
            dispatch(requestSuccess({ method }));
            return Promise.reject(err);
          }
        }

        if (status === 403 && resErrorCode === ERROR_CODES.CAPTCHA_V2_VERIFICATION_REQUIRED) {
          dispatch({ type: REQUIRE_V2_CAPTCHA_VERIFICATION });
          dispatch(resetRetryCounter({ method }));
          dispatch(requestSuccess({ method }));
          return Promise.reject(err);
        }

        if (status === 403 && resErrorCode === ERROR_CODES.JWT_INVALID) {
          dispatch(refresh());
          return Promise.reject(err);
        }

        piwik.trackEvent({ ...eventTags.technical_error, event_label: resErrorCode });

        dispatch(
          requestError({
            method,
            errorCode: errorCode || resErrorCode,
            statusCode: status,
            errorMessage: resErrorMessage,
          })
        );
      } else {
        piwik.trackEvent({ ...eventTags.technical_error, event_label: errorCode });
        dispatch(requestError({ method: ctx.method, errorCode }));
      }
      throw err;
    });
};

const sessionError = () => ({ type: SESSION_ERROR });

const authSuccess =
  (payload = {}) =>
    (dispatch, getState) => {
      const { access_token, token_type, scope, expires_in } = payload; // eslint-disable-line camelcase
      const expiresAt = new Date(new Date().getTime() + expires_in * 1000); // eslint-disable-line camelcase
      const data = {
        accessToken: access_token,
        tokenType: token_type,
        scope,
        expiresAt,
      };
      dispatch({ type: AUTH_SUCCESS, payload: data });
      const state = getState();
      const session = getSession(state);
      const auth = getAuth(state);
      webStorage.setAuth(session, auth);
      return auth;
    };

const SCOPES = { APPROVED, AUTHENTICATED, IDENTIFIED };
const getScopeType = scope => SCOPES[scope];

/* ---- Authentication and Captcha ---- */

export const authenticate = () => (dispatch, getState) =>
  requestCoreApi(dispatch, { method: coreApi.endpoints.authenticate }, { errorCode: errors.AUTH_ERROR, refreshAuthToken: false }).then(
    payload => {
      const auth = dispatch(authSuccess(payload));

      const scope = getScopeStatus(getState());
      if (!scope) {
        throw new Error('Unknown scope');
      }

      dispatch({ type: getScopeType(scope) });
      return auth;
    }
  );

export const requestOTP = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.requestOTP, {
    includeJwt: false,
    includeCaptchaJwt: true,
  });

  const data = {
    phone: getPhoneNumber(state),
    plateNumber: getPlateNumber(state),
  };

  return requestCoreApi(dispatch, { method, auth, version, data }).then(() => dispatch({ type: OTP_REQUESTED }));
};

const createJwtRequest = (endpoint, dataFn, errorHandler) => () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, endpoint, {
    includeJwt: false,
    includeCaptchaJwt: true,
  });

  const data = dataFn(state);

  return requestCoreApi(dispatch, { method, auth, version, data })
    .then(({ jwt, token }) => {
      const session = getSession(state);
      const jwtToken = jwt || token;
      webStorage.setJwt(session, jwtToken);
      return dispatch({ type: JWT_CREATED, payload: { jwt: jwtToken } });
    })
    .catch(
      errorHandler?.(dispatch) ||
        (err => {
          throw err;
        })
    );
};

export const requestIdentification = createJwtRequest(
  coreApi.endpoints.requestIdentification,
  state => ({
    phoneNumber: getPhoneNumber(state),
    plateNumber: getPlateNumber(state),
    claimId: getClaimId(state) || getPreviousClaimId(state),
  }),
  dispatch => err => {
    if (err?.response?.data?.errorCode === ERROR_CODES.VERIFICATION_MAX_ATTEMPTS_EXCEEDED || err?.response?.data?.attemptsRemaining === 0) {
      dispatch({ type: IDENTIFICATION_REQUEST_EXCEEDED, error: err });
    }
    throw err;
  }
);

export const generateJWT = createJwtRequest(coreApi.endpoints.getJWT, state => ({
  phone: getPhoneNumber(state),
  otp: getOtp(state),
}));

export const clearJWT = () => ({ type: CLEAR_JWT });

export const generateV2CaptchaJwt = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getCaptchaJWT, {
    includeJwt: false,
  });

  const phoneNumber = getPhoneNumber(state);
  const token = getCaptchaV2Token(state);
  const data = {
    token,
    version: CAPTCHA_VERSION.V2,
    phoneNumber,
  };

  return requestCoreApi(dispatch, { method, auth, version, data }).then(({ token: captchaJwt }) => {
    dispatch({ type: CLEAN_UP_REQUIRED_V2_CAPTCHA_VERIFICATION });
    return dispatch({ type: CAPTCHA_JWT_CREATED, captchaJwt });
  });
};

export const generateV3CaptchaJwt = (action, customPhoneNumberFn) => () => async (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getCaptchaJWT, {
    includeJwt: false,
  });

  const captchaToken = await requestCaptchaToken(state, action);
  const phoneNumber = customPhoneNumberFn?.(state) ?? getPhoneNumber(state);

  const data = {
    token: captchaToken,
    version: CAPTCHA_VERSION.V3,
    action,
    phoneNumber,
  };

  return requestCoreApi(dispatch, { method, auth, version, data }).then(({ token: captchaJwt }) =>
    dispatch({ type: CAPTCHA_JWT_CREATED, captchaJwt })
  );
};

/* ---- Product ---- */

export const getProductConfiguration = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getProductConfiguration);

  const claim = getClaim(state);
  const productCode = claim?.subscription?.product?.productCode;

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    productCode,
  }).then(configuration => dispatch({ type: PRODUCT_CONFIGURATION_LOADED, configuration }));
};

export const getClaimOperation = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getClaimOperation);

  const claimId = getClaimId(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
  }).then(claimOperation => !claimOperation.errorCode && dispatch({ type: CLAIM_OPERATION_LOADED, claimOperation }));
};

export const getEventAdditionalQuestions = eventCode => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getEventAdditionalQuestions);

  if (!getIsPublicApiV5(state)) {
    return Promise.resolve();
  }

  const claimId = getClaimId(state);

  if (!claimId) {
    return;
  }

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    eventCode,
  })
    .then(eventAdditionalQuestions =>
      dispatch({ type: EVENT_ADDITIONAL_QUESTIONS_LOADED, eventAdditionalQuestions: eventAdditionalQuestions || [] })
    )
    .catch(() => dispatch({ type: EVENT_ADDITIONAL_QUESTIONS_LOADED, eventAdditionalQuestions: [] }));
};

/* ---- Claim, Location, Highway, Eligibility, Immobilization ---- */

export const createClaim = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createClaim);

  const isPublicApiV5 = getIsPublicApiV5(state);

  const shouldSendVinNumber = getDisplayVinNumber(state) && isVinMandatory(state);
  let data = {
    leaserContractNumber: getLeaserContractNumber(state),
    vehicle: {
      mileage: Number(getMileage(state)) || undefined,
      plateNumber: getPlateNumber(state),
      vin: getVinNumber(state) || undefined,
    },
    driver: {
      firstName: getFirstName(state),
      lastName: getLastName(state),
      phoneMobile1: getPhoneNumber(state),
    },
    isVinMandatory: shouldSendVinNumber,
  };
  if (getBrand(state)) {
    data = {
      ...data,
      vehicle: {
        ...data.vehicle,
        country: getCountry(state),
        make: getBrand(state),
        model: getModel(state),
        type: getType(state),
        weight: getWeight(state),
        registrationDate: getRegistrationDate(state),
      },
    };
  }
  if (isPublicApiV5) {
    data = {
      ...data,
      vehicle: {
        ...data.vehicle,
        energy: getEnergy(state),
        energyType: getEnergyType(state),
      },
    };
  }

  return requestCoreApi(dispatch, { method, version, data, auth }).then(claim => {
    dispatch({ type: CLAIM_CREATED, claim });

    const updatedState = getState();
    const newClaim = getClaim(updatedState);
    const session = getSession(updatedState);
    webStorage.setClaimId(session, newClaim);

    return newClaim;
  });
};

export const putClaimEvent = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.putClaimEvent);

  const data = {
    ...getEvent(state),
    ...getEventDetails(state),
  };
  const claimId = getClaimId(state);

  dispatch({ type: PUT_CLAIM });
  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(() =>
    dispatch({ type: CLAIM_PUTTED, claim: { event: data } })
  );
};

export const putClaimLocation = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.putClaimLocation);

  const claimId = getClaimId(state);
  const location = getLocation(state);
  const position = getClaimPosition(state);
  const address = getClaimAddress(state);
  const data = { position, address, ...location };

  dispatch({ type: PUT_CLAIM });
  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    data,
  }).then(() => dispatch({ type: CLAIM_PUTTED, claim: { location: data } }));
};

export const searchClaim = (plateNumber, vin, phoneNumber) => (dispatch, getState) => {
  const { auth, method, version } = buildApiContext(getState, coreApi.endpoints.searchClaim);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    data: { plateNumber, vin, phoneNumber },
  });
};

export const loadClaimByPlateNumber = () => (dispatch, getState) => {
  const state = getState();
  let session = getSession(state);

  const plateNumber = getPlateNumber(state);
  const shouldSendVinNumber = getDisplayVinNumber(state) && isVinMandatory(state);
  const vin = shouldSendVinNumber ? getVinNumber(state) : null;

  return dispatch(searchClaim(plateNumber, vin)).then(claims => {
    if (!claims.numberOfElements) {
      return dispatch({ type: NO_CLAIM_FOUND });
    }

    const claim = claims.content[0];
    if (differenceInDays(new Date(), new Date(claim.creationDate)) > 7) {
      return dispatch({ type: NO_CLAIM_FOUND });
    }
    dispatch({ type: CLAIM_LOADED, claim });

    const updatedState = getState();
    const newClaim = getClaim(updatedState);
    session = getSession(updatedState);
    webStorage.setClaimId(session, newClaim);
    return claim;
  });
};

export const searchClaimById = id => (dispatch, getState) => {
  const { auth, method, version } = buildApiContext(getState, coreApi.endpoints.searchClaimById);

  return requestCoreApi(dispatch, { method, auth, version, data: { id } });
};

export const loadClaimById = () => async (dispatch, getState) => {
  const state = getState();
  let session = getSession(state);

  const claimId = getPreviousClaimId(state) || getClaimId(state);
  const claim = await dispatch(searchClaimById(claimId));

  dispatch({ type: CLAIM_LOADED, claim });

  const updatedState = getState();
  const newClaim = getClaim(updatedState);
  session = getSession(updatedState);

  if (session !== null && newClaim !== null) {
    webStorage.setClaimId(session, newClaim);
  }
  return claim;
};

export const getClaimHistoryDispatch = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getClaimHistory);

  const claimId = getClaimId(state);

  return requestCoreApi(dispatch, { method, auth, version, claimId }).then(history => dispatch({ type: CLAIM_HISTORY_DISPATCH, history }));
};

export const checkCscEligibility = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.checkCscEligibility);

  const claimId = getClaimId(state);
  const benefitCode = getBenefit(state) || BENEFITS.REPLACEMENT_CAR;
  const businessPartnerCode = getBusinessPartnerCode(state);
  const eventCode = getEventCode(state);
  const requestedDatetime = new Date();

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    data: { benefitCode, businessPartnerCode, eventCode, requestedDatetime },
  }).then(response => !response.errorCode && dispatch({ type: CSC_ELIGIBILE, response }));
};

export const storeSelectedBenefit = ({ payload }) => ({ type: SELECT_BENEFIT, payload });
export const clearSelectedBenefit = () => ({ type: CLEAR_BENEFIT });

export const updateImmobilizationGarage = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.updateImmobilizationGarage);

  const claimId = getClaimId(state);
  const garageName = getRentalVehicleOtherGarageName(state);
  const garageEmail = getRentalVehicleOtherGarageEmail(state);
  const garageAddress = getRentalVehicleOtherLocationAddressObject(state);
  const garagePhone = getRentalVehicleOtherGaragePhone(state);
  const { position: location, ...rest } = garageAddress;
  const locationDetails = { location, ...rest };
  const data = { name: garageName, email: garageEmail, locationDetails, phoneNumber: garagePhone };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    data,
  }).then(response => !response.errorCode && dispatch({ type: IMMOBILISATION_GARAGE_UPDATED }));
};

export const npsEligibilityClaim = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.npsEligibilityClaim);

  const claimId = getClaimId(state);

  return requestCoreApi(dispatch, { method, auth, version, claimId }).then(npsConsent => {
    dispatch({ type: NPS_ELIGIBILITY_CLAIM, payload: { npsConsent } });
    return npsConsent;
  });
};

export const putNpsEligibilityClaim = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.putNpsEligibilityClaim);

  const claimId = getClaimId(state);
  const npsAnswer = getNpsAnswer(state);
  if (!claimId) {
    return;
  }
  const data = {
    npsAnswer,
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(() => dispatch({ type: PUT_NPS_ELIGIBILITY_CLAIM }));
};

export const getBrandSuggestions = (hint, setSuggestions) => (dispatch, getState) => {
  const { auth, method, version } = buildApiContext(getState, coreApi.endpoints.getBrandSuggestions);

  return requestCoreApi(dispatch, { method, auth, version, hint }).then(results => {
    const suggestions = map(result => ({ label: result }), results);
    return setSuggestions(suggestions);
  });
};

export const getModelSuggestions = (brand, setSuggestions) => (dispatch, getState) => {
  const { auth, method, version } = buildApiContext(getState, coreApi.endpoints.getModelSuggestions);

  return requestCoreApi(dispatch, { method, auth, version, brand }).then(results => {
    const suggestions = map(({ model, type, weight }) => ({ label: model, type, weight }), results);
    return setSuggestions(suggestions);
  });
};

export const getHighwaySections = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getHighwaySections);

  const source = { position: getPosition(state), metres: getHighwayMetres(state) };
  const { lat, lng } = source.position;
  const { metres } = source;
  const queryValues = { lat, lng, metres };

  return requestCoreApi(dispatch, { method, auth, version, ...queryValues }).then(sections => {
    if (isEmpty(sections)) {
      return dispatch({ type: HIGHWAY_SECTIONS_NOT_FOUND });
    }
    dispatch({ type: HIGHWAY_SECTIONS_FOUND, sections });
    return sections;
  });
};

export const fetchHighwaysFromReferential = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.fetchHighways);

  const { lng, lat } = getPosition(state);
  const queryValues = {
    lng,
    lat,
  };

  return requestCoreApi(dispatch, {
    method,
    version,
    auth,
    ...queryValues,
  }).then(response => {
    if (!response.error) {
      if (isEmpty(response)) {
        dispatch({ type: HIGHWAYS_NOT_FOUND });
        return;
      }

      dispatch({ type: STORE_HIGHWAYS, payload: response });
    }
    return { found: false, error: response.error };
  });
};

export const clearReferentialHighways = () => dispatch => dispatch({ type: CLEAR_HIGHWAYS_STATE, payload: undefined });

export const fetchHighwayDirectionsFromReferential = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.fetchHighwayDirections);

  const highwayCode = getHighwayCode(state);
  const { lng, lat } = getPosition(state);
  const queryValues = {
    highwayCode,
    lng,
    lat,
  };

  return requestCoreApi(dispatch, {
    method,
    version,
    auth,
    ...queryValues,
  }).then(response => !response.error && dispatch({ type: STORE_HIGHWAY_DIRECTIONS, payload: response }));
};

export const fetchHighwayPOIsFromReferential = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.fetchHighwayPOIs);

  const highwayCode = getHighwayCode(state);
  const directionCode = getHighwayDirection(state);
  const { lng, lat } = getPosition(state);
  const queryValues = {
    highwayCode,
    directionCode,
    lng,
    lat,
  };

  return requestCoreApi(dispatch, {
    method,
    version,
    auth,
    ...queryValues,
  }).then(response => !response.error && dispatch({ type: STORE_HIGHWAY_POINT_OF_INTERESTS, payload: response }));
};

export const getVehicleInfo = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.searchVehicleInfo);

  const shouldSendVinNumber = getDisplayVinNumber(state) && isVinMandatory(state);
  const plateNumber = getPlateNumber(state);
  const vin = getVinNumber(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    plateNumber,
    vin,
    isVinMandatory: shouldSendVinNumber,
  }).then(vehicle => {
    dispatch({ type: VEHICLE_INFO_LOADED, vehicle });
  });
};

export const putClaimVehicleLoss = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.putClaimVehicleLoss);

  const claimId = getClaimId(state);
  const { CUSTOMER_WILL_ANSWER_LATER } = FNOL_CUSTOMER_ANSWER;
  const defaultData = {
    customerAnswer: CUSTOMER_WILL_ANSWER_LATER,
    callback: true,
  };
  const fnol = getFnol(state);
  const data = isEmpty(fnol) ? defaultData : fnol;

  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(() => dispatch({ type: CLAIM_VEHICLE_LOSS_PUTTED }));
};

export const getInsuranceCompanies = name => (dispatch, getState) => {
  const { auth, method, version } = buildApiContext(getState, coreApi.endpoints.getInsuranceCompanies);

  return requestCoreApi(dispatch, { method, auth, version, name });
};

/* ---- Digital Channel ---- */

export const digitalInteractions = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.digitalInteractions);

  const shouldSendVinNumber = getDisplayVinNumber(state) && isVinMandatory(state);
  const vin = shouldSendVinNumber ? getVinNumber(state) : null;

  const data = {
    callback: true,
    driver: {
      firstName: getFirstName(state),
      lastName: getLastName(state),
      contact: {
        phoneMobile1: getPhoneNumber(state),
      },
    },
    vehicle: {
      plateNumber: getPlateNumber(state),
      country: getLocationCountry(state),
      ...(vin && { vin }),
    },
    event: {
      ...getEvent(state),
      ...getEventDetails(state),
    },
    location: getLocation(state),
  };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    data,
  }).then(() => {
    dispatch({ type: DIGITAL_INTERACTIONS });
  });
};

export const createClaimDigitalExit = customCode => () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createClaimDigitalExit);

  const claimId = getClaimId(state);
  if (!claimId) {
    return;
  }

  let errorCode = getErrorCode(state);
  const { INTERNAL_SERVER_ERROR, TECHNICAL_ERROR } = ERROR_CODES;
  if (errorCode === INTERNAL_SERVER_ERROR) {
    errorCode = TECHNICAL_ERROR;
  }

  const exitReason = customCode || errorCode;
  const callback = includes(exitReason, digitalExitReasonsToBeCalledBack);

  piwik.trackEvent({ ...eventTags.digital_exit, event_label: exitReason });

  const data = {
    callback,
    exitReason,
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(() => dispatch({ type: CLAIM_DIGITAL_EXIT_CREATED }));
};

export const createMissionDigitalExit = customCode => () => (dispatch, getState) => {
  const state = getState();
  const missionId = Number(getMissionId(state));

  createMissionDigitalExitLogic(missionId, dispatch, state, customCode);
};

export const createRentalMissionDigitalExit = customCode => () => (dispatch, getState) => {
  const state = getState();
  const rentalMissionId = getCoreRentalMissionId(state);

  return createMissionDigitalExitLogic(rentalMissionId, dispatch, state, customCode);
};

const createMissionDigitalExitLogic = (missionId, dispatch, state, customCode) => {
  const { auth, method, version } = buildApiContext(() => state, coreApi.endpoints.createMissionDigitalExit);

  const claimId = getClaimId(state);
  if (!(claimId && missionId)) {
    return;
  }

  let errorCode = getErrorCode(state);
  const { INTERNAL_SERVER_ERROR, TECHNICAL_ERROR } = ERROR_CODES;
  if (errorCode === INTERNAL_SERVER_ERROR) {
    errorCode = TECHNICAL_ERROR;
  }

  const exitReason = customCode || errorCode;
  const callback = includes(exitReason, digitalExitReasonsToBeCalledBack);

  piwik.trackEvent({ ...eventTags.digital_exit, event_label: exitReason });

  const data = {
    callback,
    exitReason,
  };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
    data,
  });
};

/* ---- Mission (1st and common) ---- */

const handleMissionCreated = (dispatch, mission) => {
  if (isTOWMission(mission)) {
    return Promise.all([dispatch(getCarDestinations(mission)), dispatch(getCarDestinationsRules(mission))]).then(() => {
      dispatch({ type: MISSION_CREATED, mission });
      return mission;
    });
  }
  if (isROSMission(mission)) {
    return dispatch(getCarDestinationsRules(mission)).then(carDestinationsRules => {
      if (carDestinationsRules?.limits?.allowHidingDestinationProcessInDigital) {
        return dispatch(getCarDestinations(mission)).then(() => {
          dispatch({ type: CAR_DESTINATIONS_RULES, rules: carDestinationsRules });
          dispatch({ type: MISSION_CREATED, mission });
          return mission;
        });
      }
      dispatch({ type: MISSION_CREATED, mission });
      return mission;
    });
  }
  dispatch({ type: MISSION_CREATED, mission });
  return mission;
};

export const createMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createMission);

  const claimId = getClaimId(state);
  dispatch({ type: CREATE_MISSION });
  const data = {
    missionType: MISSION_TYPE.IMMEDIATE,
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(mission => handleMissionCreated(dispatch, mission));
};

export const createOemMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createOemMission);
  const claimId = getClaimId(state);
  const data = { missionType: MISSION_TYPE.IMMEDIATE };
  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(mission => handleMissionCreated(dispatch, mission));
};

export const transferToOem = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.transferToOem);
  const claimId = getClaimId(state);
  dispatch({ type: TRANSFER_TO_OEM });
  return requestCoreApi(dispatch, { method, auth, version, claimId }).then(oemProvider => {
    dispatch({ type: OEM_TRANSFERRED, oemProvider });
  });
};

export const searchMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getAllMissions);

  const claim = getClaim(state);

  return requestCoreApi(dispatch, { method, auth, version, claimId: claim.id }).then(missions => {
    const missionTowOrRos = lastMissionTowOrRos(missions);
    if (missions && missions.length) {
      dispatch({ type: MISSIONS_LOADED, missions });
      if (missionTowOrRos) {
        dispatch({ type: MISSION_LOADED, mission: missionTowOrRos });
      } else {
        dispatch({ type: NO_MISSION_FOUND });
      }
    } else {
      dispatch({ type: NO_MISSION_FOUND });
    }
  });
};

export const searchMissionByPreviousId = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getAllMissions);

  const previousMissionId = getPreviousMissionId(state);
  const claim = getClaim(state);

  return requestCoreApi(dispatch, { method, auth, version, claimId: claim.id }).then(missions => {
    const myMission = find(propEq('id', previousMissionId))(missions);
    if (missions && missions.length && myMission) {
      dispatch({ type: MISSION_LOADED, mission: myMission });
    } else {
      dispatch({ type: NO_MISSION_FOUND });
    }
  });
};

export const cancelMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.cancelMission);

  const claimId = getClaimId(state);
  const missionId = getMissionId(state);

  dispatch({ type: CANCEL_MISSION });

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
  }).then(data => {
    dispatch({ type: MISSION_CANCELED });
    return data;
  });
};

export const cancelMissionCost = missionId => async (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.cancelMissionCost);

  const claimId = getClaimId(state);

  const data = await requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
  });

  return data;
};

export const automaticCancelMission = missionIdToCancel => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.automaticCancelMission);

  const claimId = getClaimId(state);
  const missionId = missionIdToCancel || getMissionId(state);
  const isRentalMission = missionId === getCoreRentalMissionId(state);

  dispatch({ type: CANCEL_MISSION });

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
  }).then(data => {
    dispatch({
      type: isRentalMission ? RENTAL_MISSION_CANCELED : MISSION_CANCELED,
      id: missionId,
    });
    return data;
  });
};

export const putVehicleDestination = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.putVehicleDestination);

  const recommendedCarDestinations = getRecommendedCarDestinations(state);
  const isDestinationProcessHidden = getAllowHidingDestinationProcessInDigital(state);
  let vehicleDestination = {
    type: VEHICLE_DESINATION_TYPE.NONE,
    reason: VEHICLE_DESINATION_REASON.NO_NETWORK_AVAILABLE,
  };
  if (!isEmpty(getCarDestination(state))) {
    vehicleDestination = getCarDestination(state);
  }
  if (isDestinationProcessHidden && !isEmpty(recommendedCarDestinations)) {
    vehicleDestination = { type: VEHICLE_DESINATION_TYPE.RECOMMENDED, id: recommendedCarDestinations[0].id };
  }
  const data = { vehicleDestination };
  const providerId = vehicleDestination?.id;
  const missionVehicleDestination = {
    vehicleDestination: !isNil(providerId)
      ? {
        type: VEHICLE_DESINATION_TYPE.RECOMMENDED,
        provider: { ...find(propEq('id', providerId), recommendedCarDestinations) },
      }
      : { type: vehicleDestination.type, provider: { ...pick(['name', 'location'], vehicleDestination) } },
  };
  const claimId = getClaimId(state);
  const missionId = Number(getMissionId(state));

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    missionId,
    claimId,
    data,
  }).then(() =>
    dispatch({
      type: MISSION_PUTTED,
      mission: missionVehicleDestination,
    })
  );
};

const getCarDestinations = mission => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getCarDestinations);

  const claim = getClaim(state);

  dispatch({ type: REQUEST_CAR_DESTINATIONS });

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    missionId: mission.id,
    claimId: claim.id,
  })
    .then(destinations => {
      dispatch({ type: CAR_DESTINATIONS, destinations });
      return destinations;
    })
    .catch(err => {
      if (err.response.status === CAR_DESTINATION_NOT_FOUND) {
        return dispatch({ type: CAR_DESTINATIONS, destinations: [] });
      }
      throw err;
    });
};

export const clearVehicleDestinationValues = () => dispatch => dispatch({ type: CLEAR_VEHICLE_DESTINATION_VALUES });

export const loadCarDestinationsInfo = () => (dispatch, getState) => {
  const state = getState();
  const mission = getMission(state);

  return Promise.all([dispatch(getCarDestinations(mission)), dispatch(getCarDestinationsRules(mission))]).then(() =>
    dispatch({ type: CAR_DESTINATIONS_INFO_LOADED })
  );
};

export const getSpecificCarDestinations = data => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getSpecificCarDestinations);

  const claim = getClaim(state);
  const mission = getMission(state);

  dispatch({ type: REQUEST_SPECIFIC_CAR_DESTINATIONS });

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId: claim.id,
    missionId: mission.id,
    data,
  })
    .then(destinations => {
      dispatch({ type: CAR_DESTINATIONS, destinations });
      dispatch({ type: SPECIFIC_CAR_DESTINATIONS, destinations });
      return destinations;
    })
    .catch(err => {
      if (err.response.status === CAR_DESTINATION_NOT_FOUND) {
        return dispatch({ type: SPECIFIC_CAR_DESTINATIONS, destinations: [] });
      }
      throw err;
    });
};

const getCarDestinationsRules = mission => (dispatch, getState) => {
  const { auth, version, state } = buildApiContext(getState);

  const towMethod = coreApi.endpoints.getTowLimits;
  const rosMethod = coreApi.endpoints.getRosLimits;

  const claim = getClaim(state);

  dispatch({ type: REQUEST_CAR_DESTINATIONS_RULES });

  if (isTOWMission(mission)) {
    return requestCoreApi(dispatch, {
      method: towMethod,
      auth,
      version,
      claimId: claim.id,
      missionId: mission.id,
    }).then(rules => {
      dispatch({ type: CAR_DESTINATIONS_RULES, rules });
      return rules;
    });
  }
  if (isROSMission(mission)) {
    return requestCoreApi(dispatch, {
      method: rosMethod,
      auth,
      version,
      claimId: claim.id,
      missionId: mission.id,
    }).then(rules => rules);
  }
};

export const dispatchMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.dispatchMission);

  const claimId = getClaimId(state);
  const mission = getMission(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId: mission.id,
  }).then(() => dispatch({ type: MISSION_DISPATCHED, mission }));
};

export const updateMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.updateMission);

  const recommendedCarDestinations = getRecommendedCarDestinations(state);
  const isDestinationProcessHidden = getAllowHidingDestinationProcessInDigital(state);

  let vehicleDestination = {
    type: VEHICLE_DESINATION_TYPE.NONE,
    reason: VEHICLE_DESINATION_REASON.NO_NETWORK_AVAILABLE,
  };
  if (!isEmpty(getCarDestination(state))) {
    vehicleDestination = getCarDestination(state);
  }
  if (isDestinationProcessHidden && !isEmpty(recommendedCarDestinations)) {
    vehicleDestination = { type: VEHICLE_DESINATION_TYPE.RECOMMENDED, id: recommendedCarDestinations[0].id };
  }

  const data = {
    vehicleDestination,
  };
  const claimId = getClaimId(state);
  const mission = {
    ...getMission(state),
    vehicleDestination: !isNil(vehicleDestination?.id)
      ? {
        type: VEHICLE_DESINATION_TYPE.RECOMMENDED,
        provider: { ...find(propEq('id', vehicleDestination?.id), recommendedCarDestinations) },
      }
      : { type: vehicleDestination.type, provider: { ...pick(['name', 'location'], vehicleDestination) } },
  };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId: mission.id,
    data,
  }).then(() => dispatch({ type: MISSION_PUTTED, mission }));
};

export const updateMissionType = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.updateMissionType);

  const claimId = getClaimId(state);
  const mission = getMission(state);
  const appointment = getAppointment(state);
  const data = isEmpty(appointment)
    ? { missionType: MISSION_TYPE.IMMEDIATE, pickupTime: undefined }
    : { missionType: MISSION_TYPE.APPOINTMENT, pickupTime: { ...appointment } };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId: mission.id,
    data,
  }).then(() => dispatch({ type: MISSION_PUTTED, mission: { ...mission, ...data } }));
};

/* ---- Mission Monitoring ---- */

const extractRentalMissionAdditionalInformationFromHistoryEvent = (missionHistory, state) => {
  const acceptedHistoryItem = missionHistory?.find(
    mission => mission.type === HISTORY_EVENTS.MISSION_ACCEPTED && mission.missionId === state?.rentalMission?.id
  );
  return acceptedHistoryItem ? { requestAcceptedTimestamp: acceptedHistoryItem.timestamp } : {};
};

const extractRentalMissionAdditionalInformationFromMonitoringEvent = (event, state) => {
  if (!event?.payload?.status && state?.rentalMission?.id === event.missionId) {
    return {};
  }

  switch (event.payload.status) {
    case MISSION_STATUS.PENDING:
      return { requestSentTimestamp: event.timestamp };
    case MISSION_STATUS.ACCEPTED:
      return { requestAcceptedTimestamp: event.timestamp };
    default:
      return {};
  }
};

const mapMissionMonitoringEvent = (state, event) => {
  const rentalMissionId = getCoreRentalMission(state)?.id;
  if (rentalMissionId === event.payload.id) {
    return {
      rentalMission: {
        ...event.payload,
        ...extractRentalMissionAdditionalInformationFromMonitoringEvent(event, state),
      },
    };
  }

  const payload = {};

  if (event?.payload?.eta) {
    payload.lastEtaUpdate = event.timestamp;
  }

  return { mainMission: { ...event.payload, payload: { ...event.payload, ...payload } } };
};

export const missionHistoryState = (state, data) => {
  let parsingObject = {};
  let payload = {};
  let lastEtaUpdate = {};
  let newData = map(param => ({ ...param, timestamp: new Date(param.timestamp) }), data);

  newData = sortBy(prop('timestamp'), newData);
  return newData.reduce((previousValue, currentValue) => {
    if (currentValue?.payload?.eta) {
      lastEtaUpdate = new Date(currentValue?.timestamp).toISOString();
    }

    const rentalMissionId = getCoreRentalMissionId(state);
    if (rentalMissionId && rentalMissionId === currentValue.missionId) {
      const rentalMission = merge(previousValue?.rentalMission?.payload, currentValue.payload);

      return {
        ...previousValue,
        rentalMission: {
          ...previousValue?.rentalMission,
          ...rentalMission,
          ...extractRentalMissionAdditionalInformationFromMonitoringEvent(currentValue),
        },
      };
    }

    if (previousValue?.mainMission?.missionId !== currentValue.missionId) {
      payload = merge(payload, currentValue.payload);
      parsingObject = merge(parsingObject, currentValue);
    } else {
      payload = merge(previousValue.payload, currentValue.payload);
      parsingObject = merge(parsingObject, currentValue);
    }

    if (isEmpty(payload.currentLocation)) {
      delete payload.currentLocation;
    }

    return {
      ...previousValue,
      mainMission: {
        ...previousValue?.mainMission,
        ...parsingObject,
        payload: {
          ...previousValue?.mainMission?.payload,
          ...payload,
          lastEtaUpdate,
        },
      },
    };
  }, {});
};

export const startMissionMonitoring = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.startMissionMonitoring);

  const onError = () => {
    dispatch(requestError({ method }));
  };
  let endOfMissionTimer;
  const resetEndOfMissionTimeout = ({ eta }, timer, fn) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(fn, new Date(eta) - Date.now());
  };
  const onMessage = data => {
    if (is(Array, data)) {
      if (data.length) {
        const mergedData = missionHistoryState(state, data);
        const payload = mergedData?.mainMission?.payload;
        if (payload?.eta) {
          endOfMissionTimer = resetEndOfMissionTimeout(payload, endOfMissionTimer, () =>
            dispatch({
              type: MISSION_MONITORING_EVENT,
              id: -1,
              timestamp: new Date(),
              payload: {
                mainMission: { status: MISSION_STATUS.ETA_EXPIRED },
              },
            })
          );
        }
        dispatch({ type: MISSION_MONITORING_EVENT, payload: mergedData });
      }
    } else {
      const { id, timestamp, payload } = data;
      if (payload.eta) {
        endOfMissionTimer = resetEndOfMissionTimeout(payload, endOfMissionTimer, () =>
          dispatch({
            type: MISSION_MONITORING_EVENT,
            id: -1,
            timestamp: new Date(),
            payload: {
              mainMission: { status: MISSION_STATUS.ETA_EXPIRED },
            },
          })
        );
      }
      if (endOfMissionTimer && isMissionMonitoringIsEnded(payload.status)) {
        clearTimeout(endOfMissionTimer);
      }

      const mappedPayload = mapMissionMonitoringEvent(state, data);
      dispatch({ type: MISSION_MONITORING_EVENT, id, timestamp, payload: mappedPayload });
    }
  };

  const { id: claimId } = getClaim(state);
  const ctx = {
    method,
    auth,
    version,
    claimId,
    onMessage,
    onError,
  };
  const eventSource = coreApi(ctx);
  dispatch({ type: MISSION_MONITORING_STARTED, eventSource });
};

const endMissionMonitoring = () => (_dispatch, getState) => {
  const state = getState();
  const eventSource = getEventSource(state);
  eventSource.close();
};

/* ---- Rental ---- */

export const createRentalMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createRentalMission);

  const claimId = getClaimId(state);

  dispatch({ type: CREATE_RENTAL_MISSION });

  const data = {
    missionType: MISSION_TYPE.IMMEDIATE,
    benefitCode: BENEFITS.REPLACEMENT_CAR,
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(mission => {
    dispatch({ type: RENTAL_MISSION_CREATED, mission });
    return mission;
  });
};

export const createOemRentalMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.createOemRentalMission);
  const claimId = getClaimId(state);
  const data = { missionType: MISSION_TYPE.IMMEDIATE, benefitCode: BENEFITS.REPLACEMENT_CAR };
  return requestCoreApi(dispatch, { method, auth, version, claimId, data }).then(mission => {
    dispatch({ type: RENTAL_MISSION_CREATED, mission });
    return mission;
  });
};

export const dispatchRentalMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.dispatchMission);

  const claimId = getClaimId(state);
  const mission = getCoreRentalMission(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    missionId: mission.id,
    claimId,
  }).then(() => dispatch({ type: RENTAL_MISSION_DISPATCHED, mission }));
};

export const updateRentalMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.updateRentalMission);

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);
  const mission = {
    ...getCoreRentalMission(state),
    rentalDrivers: getRentalDrivers(state),
    rentalVehicleDetails: getRentalVehicleDetails(state),
    rentalBookingDetails: getRentalBookingDetails(state),
  };

  const data = {
    rentalDrivers: getRentalDrivers(state),
    rentalVehicleDetails: getRentalVehicleDetails(state),
    rentalBookingDetails: getRentalBookingDetails(state),
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, missionId, data }).then(() =>
    dispatch({ type: RENTAL_MISSION_UPDATED, mission })
  );
};

export const extendRentalMission = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.extendRentalMission);

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  const data = {
    durationInMinutes: getRentalExtensionDurationInMinutes(state),
  };

  return requestCoreApi(dispatch, { method, auth, version, claimId, missionId, data }).then(
    response => !response.errorCode && dispatch({ type: RENTAL_EXTENSION_SENT })
  );
};

export const getRentalCurrentLimits = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.rentalCurrentLimits);

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
  }).then(response => !response.errorCode && dispatch({ type: RENTAL_CURRENT_LIMITS, response }));
};

export const getCscSettings = () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.getCscSettings);

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
  }).then(settings => !settings.errorCode && dispatch({ type: CSC_SETTINGS_LOADED, settings }));
};

export const startRentalExtension = () => dispatch => {
  dispatch(getRentalCurrentLimits()).then(() => {
    dispatch({
      type: RENTAL_EXTENSION_WORKFLOW,
    });
  });
};

export const updateRentalExtensionInfo = info => () => (dispatch, getState) => {
  const { auth, method, version, state } = buildApiContext(getState, coreApi.endpoints.rentalExtensionInfo);

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  const data = { isEligibleForExtension: info?.isEligibleForExtension };

  return requestCoreApi(dispatch, {
    method,
    auth,
    version,
    claimId,
    missionId,
    data,
  });
};

const actions = {
  resetRetryCounter,
  retryNotFoundModal,
  retryExpiredModal,
  retryClaimExist,
  continueProcess,
  refresh,
  authenticate,
  requestOTP,
  requestIdentification,
  generateJWT,
  clearJWT,
  generateV2CaptchaJwt,
  generateV3CaptchaJwt,
  getProductConfiguration,
  getClaimOperation,
  getEventAdditionalQuestions,
  createClaim,
  putClaimEvent,
  putClaimLocation,
  searchClaim,
  loadClaimByPlateNumber,
  searchClaimById,
  loadClaimById,
  getClaimHistoryDispatch,
  checkCscEligibility,
  storeSelectedBenefit,
  clearSelectedBenefit,
  updateImmobilizationGarage,
  npsEligibilityClaim,
  putNpsEligibilityClaim,
  getBrandSuggestions,
  getModelSuggestions,
  getHighwaySections,
  fetchHighwaysFromReferential,
  fetchHighwayDirectionsFromReferential,
  fetchHighwayPOIsFromReferential,
  clearReferentialHighways,
  getVehicleInfo,
  putClaimVehicleLoss,
  getInsuranceCompanies,
  digitalInteractions,
  createClaimDigitalExit,
  createMissionDigitalExit,
  createRentalMissionDigitalExit,
  createMission,
  searchMission,
  searchMissionByPreviousId,
  cancelMission,
  automaticCancelMission,
  putVehicleDestination,
  getCarDestinations,
  clearVehicleDestinationValues,
  loadCarDestinationsInfo,
  getSpecificCarDestinations,
  getCarDestinationsRules,
  dispatchMission,
  updateMission,
  updateMissionType,
  startMissionMonitoring,
  endMissionMonitoring,
  clearMissionMonitoring,
  createRentalMission,
  dispatchRentalMission,
  updateRentalMission,
  extendRentalMission,
  getRentalCurrentLimits,
  getCscSettings,
  startRentalExtension,
  updateRentalExtensionInfo,
  transferToOem,
  createOemMission,
  createOemRentalMission,
};

export default { reducer, actions };
